import AdminLayout from 'layouts/Admin';
import { RouteObject } from 'react-router';
import { lazyWithRetries, SuspenseWrapper } from 'utils/common/lazy';

const Main = lazyWithRetries(() => import('../layouts/Main'));
const CrossSell = lazyWithRetries(() => import('../pages/CrossSell'));
const ActivityFeed = lazyWithRetries(() => import('../pages/ActivityFeed'));
const Admin = lazyWithRetries(() => import('../pages/Admin'));
const AdminReports = lazyWithRetries(() => import('../pages/AdminReports'));
const AgingConfig = lazyWithRetries(() => import('../pages/AgingConfig'));
const StaffAnnouncements = lazyWithRetries(() => import('../pages/Announcements'));
const Appointments = lazyWithRetries(() => import('../pages/Appointments'));
const AppointmentTypes = lazyWithRetries(() => import('../pages/AppointmentTypes'));
const Articles = lazyWithRetries(() => import('../pages/Articles'));
const BillingConfiguration = lazyWithRetries(() => import('../pages/BillingConfiguration'));
const CreateArticle = lazyWithRetries(() => import('../pages/CreateArticle'));
const CustomerFeedback = lazyWithRetries(() => import('../pages/CustomerFeedback'));
const FormsAndSurveys = lazyWithRetries(() => import('../pages/FormsAndSurveys'));
const MifEditPage = lazyWithRetries(() => import('../pages/FormsAndSurveys/MifEditPage'));
const FrontDeskRequestTypes = lazyWithRetries(() => import('../pages/FrontDeskRequestTypes'));
const Insurance = lazyWithRetries(() => import('../pages/Insurance'));
const InternalAutomations = lazyWithRetries(() => import('../pages/InternalAutomations'));
const InternalNoteTemplates = lazyWithRetries(() => import('../pages/InternalNoteTemplates'));
const MedicationTemplates = lazyWithRetries(() => import('../pages/MedicationTemplates'));
const EmailTemplates = lazyWithRetries(() => import('../pages/EmailTemplates'));
const EmailTemplate = lazyWithRetries(() => import('../pages/EmailTemplate'));
const SignableDocuments = lazyWithRetries(() => import('../pages/SignableDocuments'));
const SignableDocument = lazyWithRetries(() => import('../pages/SignableDocument'));
const ManageRole = lazyWithRetries(() => import('../pages/ManageRole'));
const ManageRoles = lazyWithRetries(() => import('../pages/ManageRoles'));
const MedicalAssistantReports = lazyWithRetries(() => import('../pages/MedicalAssistantReports'));
const MSRTaskDetail = lazyWithRetries(() => import('../pages/MSRTasks/MSRTaskDetail'));
const MSRTasksConfig = lazyWithRetries(() => import('../pages/MSRTasksConfig'));
const MSRTasksQueue = lazyWithRetries(() => import('../pages/MSRTasks'));
const OrderDetails = lazyWithRetries(() => import('../pages/OrderDetails/OrderDetails'));
const Orders = lazyWithRetries(() => import('../pages/Orders'));
const AiTools = lazyWithRetries(() => import('../pages/AiTools'));
const Patient = lazyWithRetries(() => import('../pages/Patient'));
const PatientAutomations = lazyWithRetries(() => import('../pages/PatientAutomations'));
const PatientMessageTemplates = lazyWithRetries(() => import('../pages/PatientMessageTemplates'));
const Patients = lazyWithRetries(() => import('../pages/Patients'));
const PhysicianReports = lazyWithRetries(() => import('../pages/PhysicianReports'));
const Prospects = lazyWithRetries(() => import('../pages/Prospects'));
const RequestMessageTemplates = lazyWithRetries(() => import('../pages/RequestMessageTemplates'));
const Reviews = lazyWithRetries(() => import('../pages/Reviews'));
const Scheduling = lazyWithRetries(() => import('../pages/Scheduling'));
const Settings = lazyWithRetries(() => import('../pages/Settings'));
const SingleArticle = lazyWithRetries(() => import('../pages/SingleArticle'));
const StaffCosts = lazyWithRetries(() => import('../pages/StaffCosts'));
const StaffFeedback = lazyWithRetries(() => import('../pages/StaffFeedback'));
const Staffs = lazyWithRetries(() => import('../pages/Staffs'));
const Tasks = lazyWithRetries(() => import('../pages/Tasks'));
const Templates = lazyWithRetries(() => import('../pages/Templates'));
const Teams = lazyWithRetries(() => import('../pages/Teams'));
const StoreProducts = lazyWithRetries(() => import('../pages/StoreProducts'));
const ShopPages = lazyWithRetries(() => import('../pages/ShopPages'));
const ShopSegments = lazyWithRetries(() => import('../pages/ShopSegments'));
const QA = lazyWithRetries(() => import('../pages/QA'));
const ClinicalTools = lazyWithRetries(() => import('../pages/ClinicalTools'));
const RefundReasons = lazyWithRetries(() => import('../pages/RefundReasons'));
const Workflows = lazyWithRetries(() => import('../pages/Workflows'));
const WorkflowDetails = lazyWithRetries(() => import('../pages/WorkflowDetails'));
const PdmpConfigs = lazyWithRetries(() => import('../pages/PdmpConfigs'));
const MonthlyAvailabilityTab = lazyWithRetries(
  () => import('../components/appointments/ManageAvailability/MonthlyAvailability')
);

const dashboardProtectedRoutesConfig: RouteObject[] = [
  { element: <SuspenseWrapper children={<Tasks />} />, path: 'tasks' },
  { element: <SuspenseWrapper children={<MSRTasksQueue />} />, path: 'msr-tasks' },
  { element: <SuspenseWrapper children={<MSRTaskDetail />} />, path: 'msr-tasks/:taskId' },
  { element: <SuspenseWrapper children={<ActivityFeed />} />, path: 'activity-feed' },

  { element: <SuspenseWrapper children={<Appointments />} />, path: 'appointments' },
  {
    element: <SuspenseWrapper children={<MonthlyAvailabilityTab showCardWrapper />} />,
    path: 'appointments/availability/:id'
  },

  { element: <SuspenseWrapper children={<Scheduling />} />, path: 'scheduling' },
  { element: <SuspenseWrapper children={<Scheduling />} />, path: 'scheduling/:view' },

  { element: <SuspenseWrapper children={<Insurance />} />, path: 'insurance/*' },

  { element: <SuspenseWrapper children={<AdminReports />} />, path: 'reports/admin' },
  {
    element: <SuspenseWrapper children={<MedicalAssistantReports />} />,
    path: 'reports/medical-assistant'
  },
  { element: <SuspenseWrapper children={<PhysicianReports />} />, path: 'reports/physician' },

  { element: <SuspenseWrapper children={<Patients />} />, path: 'patients' },
  { element: <SuspenseWrapper children={<Prospects />} />, path: 'patients/prospect' },
  { element: <SuspenseWrapper children={<Patient />} />, path: 'patient/:patientId' },

  { element: <SuspenseWrapper children={<Templates />} />, path: 'templates' },
  { element: <SuspenseWrapper children={<Articles />} />, path: 'articles' },
  { element: <SuspenseWrapper children={<SingleArticle />} />, path: 'articles/:articleId' },
  { element: <SuspenseWrapper children={<CreateArticle />} />, path: 'articles/create' },
  { element: <SuspenseWrapper children={<CreateArticle />} />, path: 'articles/update/:articleId' },

  {
    children: [
      {
        element: <SuspenseWrapper children={<Admin />} />,
        index: true
      },
      { element: <SuspenseWrapper children={<AgingConfig />} />, path: 'aging-configuration' },
      {
        element: <SuspenseWrapper children={<StaffAnnouncements />} />,
        path: 'staff-announcements'
      },
      { element: <SuspenseWrapper children={<Settings />} />, path: 'system-settings' },
      { element: <SuspenseWrapper children={<AppointmentTypes />} />, path: 'appointment-types' },
      {
        element: <SuspenseWrapper children={<FrontDeskRequestTypes />} />,
        path: 'front-desk-request-types'
      },
      {
        element: <SuspenseWrapper children={<PatientMessageTemplates />} />,
        path: 'patient-message-templates'
      },
      {
        element: <SuspenseWrapper children={<RequestMessageTemplates />} />,
        path: 'request-message-templates'
      },
      {
        element: <SuspenseWrapper children={<InternalNoteTemplates />} />,
        path: 'internal-note-templates'
      },
      { element: <SuspenseWrapper children={<MSRTasksConfig />} />, path: 'msr-tasks-config' },
      {
        element: <SuspenseWrapper children={<MedicationTemplates />} />,
        path: 'medication-templates'
      },
      { element: <SuspenseWrapper children={<PdmpConfigs />} />, path: 'pdmp-configs' },
      { element: <SuspenseWrapper children={<EmailTemplates />} />, path: 'email-templates' },
      { element: <SuspenseWrapper children={<EmailTemplate />} />, path: 'email-templates/new' },
      { element: <SuspenseWrapper children={<EmailTemplate />} />, path: 'email-templates/:id' },
      {
        element: <SuspenseWrapper children={<SignableDocuments />} />,
        path: 'signable-documents'
      },
      {
        element: <SuspenseWrapper children={<SignableDocument />} />,
        path: 'signable-documents/new'
      },
      {
        element: <SuspenseWrapper children={<SignableDocument />} />,
        path: 'signable-documents/:id'
      },
      { element: <SuspenseWrapper children={<Staffs />} />, path: 'manage-staff' },
      { element: <SuspenseWrapper children={<Teams />} />, path: 'manage-teams' },
      { element: <SuspenseWrapper children={<ManageRoles />} />, path: 'manage-roles' },
      { element: <SuspenseWrapper children={<ManageRole />} />, path: 'manage-role/:roleId' },
      {
        element: <SuspenseWrapper children={<InternalAutomations />} />,
        path: 'internal-automations'
      },
      {
        element: <SuspenseWrapper children={<PatientAutomations />} />,
        path: 'patient-automation'
      },
      { element: <SuspenseWrapper children={<StaffCosts />} />, path: 'staff-costs' },
      { element: <SuspenseWrapper children={<BillingConfiguration />} />, path: 'billing-config' },
      { element: <SuspenseWrapper children={<RefundReasons />} />, path: 'refund-reasons' },
      { element: <SuspenseWrapper children={<Reviews />} />, path: 'reviews' },
      { element: <SuspenseWrapper children={<StaffFeedback />} />, path: 'staff-feedback' },
      { element: <SuspenseWrapper children={<CustomerFeedback />} />, path: 'customer-feedback' },
      { element: <SuspenseWrapper children={<StoreProducts />} />, path: 'manage-products' },
      { element: <SuspenseWrapper children={<ShopPages />} />, path: 'shop-pages' },
      { element: <SuspenseWrapper children={<ShopSegments />} />, path: 'shop-segments' },
      {
        element: <SuspenseWrapper children={<FormsAndSurveys />} />,
        path: 'forms-and-surveys'
      },
      {
        element: <SuspenseWrapper children={<MifEditPage />} />,
        path: 'forms-and-surveys/mif/:idOrCode'
      }
    ],
    element: <SuspenseWrapper children={<AdminLayout />} />,
    path: 'admin'
  },

  { element: <SuspenseWrapper children={<Orders />} />, path: 'orders' },
  { element: <SuspenseWrapper children={<OrderDetails />} />, path: 'orders/:orderId' },
  { element: <SuspenseWrapper children={<AiTools />} />, path: 'ai-tools' },
  { element: <SuspenseWrapper children={<Workflows />} />, path: 'workflows' },
  { element: <SuspenseWrapper children={<WorkflowDetails />} />, path: 'workflows/:id' },
  { element: <SuspenseWrapper children={<QA />} />, path: 'qa/*' },
  { element: <SuspenseWrapper children={<ClinicalTools />} />, path: 'clinical-tools' }
];

export const protectedRoutesConfig: RouteObject[] = [
  {
    children: [
      {
        children: dashboardProtectedRoutesConfig,
        element: <SuspenseWrapper children={<Main />} />,
        path: '*'
      }
    ],
    path: 'dashboard'
  },
  { element: <SuspenseWrapper children={<CrossSell />} />, path: 'cross-sell' }
];
